import { create } from 'zustand';
import { PaginationType } from '../types/pagination';
import {
  fetchTaskList,
  FetchTaskListRequest,
  postTask,
  PostTaskRequest,
  TaskType,
  updateTaskExecutor,
} from './api/tasks-api';
import { useAlertStore } from './use-alert-store';
import { useEmployeesStore } from './use-employee-store';

interface Store {
  isLoadingFetch?: boolean;
  list?: TaskType[];
  fetchTaskList: (params: FetchTaskListRequest) => Promise<void>;
  fake?: TaskType[];
  process?: TaskType[];
  done?: TaskType[];
  updateTaskExecutor: (task_id: number, params: { executor_id: number }) => Promise<void>;
  createTask: (params: PostTaskRequest) => Promise<void>;
  handleUpdateTasks: (payload: TaskType) => void;
  pagination?: PaginationType;
}

export const useTaskStore = create<Store>((set, get) => ({
  fetchTaskList: async (params) => {
    set({ isLoadingFetch: true });
    try {
      const { tasks, new: process, fake, done, ...other } = await fetchTaskList(params);

      const isInfinite = !!params.infinite;
      const updatedTasks = isInfinite ? get().list?.concat(tasks) : tasks;
      set({ list: updatedTasks, process, fake, done, pagination: other });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по задачам', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },

  createTask: async (params) => {
    try {
      set({
        isLoadingFetch: true,
      });

      const response = await postTask(params);
      const tasks = response.data;

      set((store) => ({
        ...store,
        list: [...tasks, ...store.list!],
      }));

      useAlertStore.getState().add({ label: 'Задача успешно создана', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось создать задачу', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },

  updateTaskExecutor: async (task_id: number, params: { executor_id: number }) => {
    try {
      set({
        isLoadingFetch: true,
      });

      await updateTaskExecutor(task_id, params);

      const employees = useEmployeesStore.getState().staff;
      const employee = employees?.find((item) => item.id === params.executor_id);

      if (employee) {
        set((store) => ({
          ...store,
          list: store.list?.map((item) =>
            item.id === task_id
              ? { ...item, received_by: { id: employee.id, name: employee.name } }
              : item,
          ),
        }));
      }

      useAlertStore.getState().add({ label: 'Задача изменена', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось обновить данные по задаче', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },

  handleUpdateTasks: (payload) => {
    set((state) => {
      const isExist = state.list?.some((item) => item.id === payload.id);
      const isSameTaskType = state.list?.[0]?.type === payload.type;

      if (!isExist && state.list && isSameTaskType) {
        return { list: [payload, ...state.list] };
      }
      const tasks = state.list?.map((item) => (item.id === payload.id ? payload : item));
      return { list: tasks };
    });
  },
}));
